import * as React from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface DatePickerProps {
  value?: Date
  onChange: (date?: Date) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  minDate?: Date
  maxDate?: Date
  /** Override the start month for the year dropdown selector */
  startMonth?: Date
  /** Override the end month for the year dropdown selector */
  endMonth?: Date
  /** Custom trigger element (rendered via `asChild`); replaces the default button. */
  trigger?: React.ReactNode
}

function useStableMonth(date: Date | undefined, fallbackYear: number, fallbackMonth: number) {
  const year = date?.getFullYear() ?? fallbackYear
  const month = date?.getMonth() ?? fallbackMonth
  return React.useMemo(() => new Date(year, month, 1), [year, month])
}

/**
 * DatePicker - Calendar popover for selecting dates
 *
 * Features:
 * - Calendar popover with dropdown month/year navigation
 * - Displays selected date in "MMM d, yyyy" format
 * - Customizable placeholder text
 * - Auto-closes on date selection
 */
export function DatePicker({
  value,
  onChange,
  disabled = false,
  placeholder = 'Pick a date',
  className,
  minDate,
  maxDate,
  startMonth: startMonthProp,
  endMonth: endMonthProp,
  trigger,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)

  // DayPicker treats startMonth/endMonth by identity. `new Date(...)` on every
  // render (and callers that pass a fresh Date for the same month) remounts the
  // caption dropdowns. Those Radix refs compose a useState setter, so React 19
  // cleanup (`setState(null)`) + re-attach loops until update depth #185.
  const currentYear = new Date().getFullYear()
  const startMonth = useStableMonth(startMonthProp, currentYear - 1, 0)
  const endMonth = useStableMonth(endMonthProp, currentYear + 3, 11)

  const minTime = minDate?.getTime()
  const maxTime = maxDate?.getTime()
  const disabledMatcher = React.useMemo(() => {
    if (minTime != null || maxTime != null) {
      return [
        ...(minTime != null ? [{ before: new Date(minTime) }] : []),
        ...(maxTime != null ? [{ after: new Date(maxTime) }] : []),
      ]
    }
    return disabled
  }, [minTime, maxTime, disabled])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !value && 'text-muted-foreground',
              className
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, 'MMM d, yyyy') : <span>{placeholder}</span>}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange(date)
            setOpen(false)
          }}
          disabled={disabledMatcher}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
        />
      </PopoverContent>
    </Popover>
  )
}
